import React, { useEffect, useRef, useState } from 'react';
import DistributorHeldOrder from '../../components/distributor/DistributorHeldOrder';
import StatusBadge from '../../components/StatusBadge';
import ShippingDocuments from '../../components/ShippingDocuments';
import orderService from '../../services/orderService';
import shipmentService from '../../services/shipmentService';
import productService from '../../services/productService';
import { shippingId } from '../../services/shippingDocumentService';
import { useLocale, useT } from '../../i18n/LanguageContext';
import { formatDeliveryDate } from '../../utils/orderDates';
import { money } from '../../utils/money';
import { productLabel } from '../../utils/productLabel';
import { requestErrorMessage } from '../../utils/requestError';
import OrderTrackingDetails from './OrderTrackingDetails';
import { isHumanNote } from './orderHistory';
import { DirectOrderCancelButton, DirectOrderShippingNote } from './DirectOrderShippingControls';
import useAccessibleDialog from '../../hooks/useAccessibleDialog';
import {
  DistributorOrderMetadata,
  DistributorOrderHistory,
  DistributorSaleDetails,
} from './DistributorOrderDetails';
const TRACKING_ERRORS = new Set([
  'Check the FedEx tracking number against the shipping notice.',
  'Check the UPS tracking number against the shipping notice.',
  'Enter a valid tracking number.',
]);
const hasControlCharacters = (value) =>
  Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
export default function OrderDetailsModal({
  order,
  onClose,
  onAction,
  canAdminister = true,
  adminDistributorId,
}) {
  const t = useT();
  const locale = useLocale();
  const [note, setNote] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippingCarrier, setShippingCarrier] = useState('');
  const [shippingError, setShippingError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [editableLineItems, setEditableLineItems] = useState([]);
  const [selectedProductToAdd, setSelectedProductToAdd] = useState('');
  const requestClose = () => {
    if (!actionLoading) onClose();
  };
  const { dialogRef, titleId } = useAccessibleDialog({
    open: !!order,
    onClose: requestClose,
    closeDisabled: actionLoading,
  });
  const orderId = order?._id;
  const orderAtOpen = useRef(order);
  if (orderAtOpen.current?._id !== orderId) orderAtOpen.current = order;
  useEffect(() => {
    const openedOrder = orderAtOpen.current;
    if (openedOrder) {
      setNote('');
      setTrackingNumber('');
      const storedCarrier =
        typeof openedOrder.carrier === 'string' ? openedOrder.carrier.trim().toLowerCase() : '';
      setShippingCarrier(['fedex', 'ups'].includes(storedCarrier) ? storedCarrier : '');
      setShippingError('');
      setActionLoading(false);
      setRejectionReason('');
      setSelectedProductToAdd('');
      const items =
        openedOrder.lineItems && openedOrder.lineItems.length > 0
          ? openedOrder.lineItems
          : [{ product: openedOrder.product, quantity: openedOrder.quantity }];
      setEditableLineItems(
        items.map((item) => ({
          _id: item._id,
          product: item.product,
          quantity: item.quantity,
          size: item.size,
          shippedQuantity: item.shippedQuantity,
        }))
      );
      if (
        canAdminister &&
        openedOrder.businessContext !== 'distributor' &&
        ['submitted', 'pending', 'approved'].includes(openedOrder.status)
      )
        productService
          .getActiveProducts()
          .then((res) => {
            const data = res.data?.data?.data || res.data?.data || [];
            setAllProducts(Array.isArray(data) ? data : []);
          })
          .catch(() => setAllProducts([]));
    }
  }, [orderId, canAdminister]);
  if (!order) return null;
  const salesRepName = order.salesRep
    ? `${order.salesRep.firstName || ''} ${order.salesRep.lastName || ''}`.trim()
    : '';
  const initials = salesRepName
    .split(' ')
    .map((n) => n[0] || '')
    .join('');
  const isSubmitted = order.status === 'submitted' || order.status === 'pending';
  const isApproved = order.status === 'approved';
  const isDistributor = order.businessContext === 'distributor';
  const adminEditable = canAdminister && !isDistributor && (isSubmitted || isApproved);
  const buildLineItemsPayload = () =>
    editableLineItems.map((item) => {
      const entry = {
        product: item.product?._id || item.product,
        quantity: Number(item.quantity),
      };
      if (item._id) entry._id = item._id;
      if (item.size) entry.size = item.size;
      return entry;
    });
  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const payload = { status: 'approved' };
      if (note.trim()) payload.note = note.trim();
      const lineItems = buildLineItemsPayload();
      if (lineItems.length > 0) payload.lineItems = lineItems;
      await orderService.updateOrder(order._id, payload);
      onAction();
      onClose();
    } catch (err) {
      alert(t(requestErrorMessage(err, 'Action failed')));
    } finally {
      setActionLoading(false);
    }
  };
  const handleShip = async () => {
    setShippingError('');
    if (!trackingNumber.trim()) {
      alert(t('Please enter a tracking number'));
      return;
    }
    if (!['fedex', 'ups', 'other'].includes(shippingCarrier)) {
      setShippingError('Choose a carrier before shipping this order.');
      return;
    }
    const enteredTracking = trackingNumber.trim();
    const invalidTracking =
      trackingNumber.length > 1000 || hasControlCharacters(trackingNumber)
        ? 'Enter a valid tracking number.'
        : shippingCarrier === 'fedex' &&
            !/^(?:\d{12}|\d{14}|\d{15}|\d{20}|\d{22})$/.test(enteredTracking)
          ? 'Check the FedEx tracking number against the shipping notice.'
          : shippingCarrier === 'ups' &&
              (/^1Z/i.test(enteredTracking)
                ? !/^1Z[A-Z0-9]{16}$/i.test(enteredTracking)
                : !/^[A-Z0-9]{7,34}$/i.test(enteredTracking))
            ? 'Check the UPS tracking number against the shipping notice.'
            : '';
    if (invalidTracking) {
      setShippingError(invalidTracking);
      return;
    }
    setActionLoading(true);
    try {
      // Omitting shippedItems marks every ordered line as fully shipped; partial shipments are not supported.
      const payload = {
        status: 'shipped',
        trackingNumber: shippingCarrier === 'ups' ? enteredTracking.toUpperCase() : enteredTracking,
      };
      if (shippingCarrier !== 'other') payload.carrier = shippingCarrier;
      if (isDistributor) {
        await shipmentService.createShipment({
          order: order._id,
          trackingNumber: payload.trackingNumber,
          ...(payload.carrier ? { carrier: payload.carrier } : {}),
        });
      } else {
        if (note.trim()) payload.note = note.trim();
        // Approved-order quantity edits must be included when the order ships.
        const lineItems = buildLineItemsPayload();
        if (lineItems.length > 0) payload.lineItems = lineItems;
        await orderService.updateOrder(order._id, payload);
      }
      onAction();
      onClose();
    } catch (err) {
      const message = err?.response?.data?.message;
      setShippingError(
        err?.response?.status === 400 && TRACKING_ERRORS.has(message)
          ? message
          : requestErrorMessage(err, 'Could not ship this order. Please try again.')
      );
    } finally {
      setActionLoading(false);
    }
  };
  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const payload = { status: 'cancelled' };
      if (rejectionReason.trim()) payload.rejectionReason = rejectionReason.trim();
      if (note.trim()) payload.note = note.trim();
      await orderService.updateOrder(order._id, payload);
      onAction();
      onClose();
    } catch (err) {
      alert(t(requestErrorMessage(err, 'Action failed')));
    } finally {
      setActionLoading(false);
    }
  };
  const orderDate = order.createdAt
    ? new Date(order.createdAt).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : t('N/A');
  // Delivery is a date-only field; see utils/orderDates.js. Older orders may have no value.
  const deliveryDate = formatDeliveryDate(order.deliveryDate, locale) || t('Not set');
  const existingAdminNote = (() => {
    if (isDistributor && isSubmitted) return '';
    const latestNote = (order.statusHistory || [])
      .slice()
      .reverse()
      .find((h) => isHumanNote(h.note))?.note;
    return order.rejectionReason || latestNote || '';
  })();
  const handleAddProduct = () => {
    if (!selectedProductToAdd) return;
    const product = allProducts.find((p) => p._id === selectedProductToAdd);
    if (!product) return;
    if (editableLineItems.some((item) => (item.product?._id || item.product) === product._id)) {
      alert(
        t('{name} is already on this order. Change its quantity instead.', { name: product.name })
      );
      return;
    }
    setEditableLineItems([...editableLineItems, { product, quantity: 1 }]);
    setSelectedProductToAdd('');
  };

  const handleRemoveLineItem = (index) => {
    setEditableLineItems(editableLineItems.filter((_, i) => i !== index));
  };

  const handleLineItemQtyChange = (index, qty) => {
    const next = Number(qty);
    setEditableLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, quantity: next } : item))
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={requestClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative bg-white rounded-[14px] shadow-xl w-[420px] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-6 z-10"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 id={titleId} className="text-base font-semibold text-[#0f172a]">
            {t('Order Details')}
          </h2>
          <button
            type="button"
            onClick={requestClose}
            disabled={actionLoading}
            aria-label={t('Close')}
            className="w-6 h-6 flex items-center justify-center text-[#64748b] hover:text-[#0f172a] rounded-full hover:bg-gray-100"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[20px]">
              cancel
            </span>
          </button>
        </div>

        <div className="flex items-center gap-2 mb-5">
          <p className="text-sm font-semibold text-[#0f172a]">{order.orderId}</p>
          <StatusBadge
            status={
              order.businessContext === 'distributor' && order.status === 'submitted'
                ? 'Order received \u2014 processing'
                : order.status
            }
          />
        </div>

        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-medium text-[#64748b]">{t('Order Date')}</p>
            <p className="text-xs font-semibold text-[#0f172a]">{orderDate}</p>
          </div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-medium text-[#64748b]">{t('Delivery Date')}</p>
            <p className="text-xs font-semibold text-[#0f172a]">{deliveryDate}</p>
          </div>
          <div className="grid grid-cols-1 gap-y-1.5 mb-4">
            <div className="flex justify-between gap-4">
              <p className="text-[10px] font-medium text-[#64748b]">{t('Clinic')}</p>
              <p className="text-xs font-medium text-[#0f172a] text-right">
                {order.clinic?.name || '—'}
              </p>
            </div>
            <div className="flex justify-between gap-4">
              <p className="text-[10px] font-medium text-[#64748b]">{t('Customer PO')}</p>
              <p className="text-xs font-medium text-[#0f172a] text-right">
                {order.patientName || '—'}
              </p>
            </div>
            <div className="flex justify-between gap-4">
              <p className="text-[10px] font-medium text-[#64748b]">{t('Delivery Address')}</p>
              <p className="text-xs font-medium text-[#0f172a] text-right">
                {typeof order.address === 'string'
                  ? order.address
                  : !isDistributor
                    ? order.address?.street || '—'
                    : [
                        order.address?.street,
                        order.address?.city,
                        order.address?.state,
                        order.address?.zipCode,
                      ]
                        .filter(Boolean)
                        .join(', ') || '—'}
              </p>
            </div>
            {isDistributor && <DistributorOrderMetadata order={order} t={t} />}
          </div>
          <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider mb-2">
            {t('Products')}
          </p>
          {editableLineItems.map((item, idx) => {
            const hasShipped = item.shippedQuantity != null && !isSubmitted && !isApproved;
            return (
              <div key={idx} className={`py-2 ${idx > 0 ? 'border-t border-[#f0f2f5]' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#0f172a]">
                      {productLabel(item.product?.name, item.size)}
                    </p>
                    {adminEditable ? (
                      <div className="flex items-center gap-2 mt-1">
                        <label className="text-[10px] text-[#64748b] whitespace-nowrap">
                          {t('Qty:')}
                        </label>
                        <input
                          type="number"
                          aria-label={t('Quantity for {name}', {
                            name: productLabel(item.product?.name, item.size),
                          })}
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleLineItemQtyChange(idx, e.target.value)}
                          className="w-[60px] h-[28px] border border-[#e2e8f0] rounded-[6px] px-2 text-xs text-[#0f172a] text-center focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(idx)}
                          aria-label={t('Remove {name}', {
                            name: productLabel(item.product?.name, item.size),
                          })}
                          className="text-[#f23e41] hover:text-[#d93235] ml-1"
                          title={t('Remove item')}
                        >
                          <span className="material-symbols-outlined text-[16px]">close</span>
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="text-[10px] text-[#64748b]">
                          {t('Ordered Qty: {n}', { n: item.quantity })}
                        </p>
                        {hasShipped && (
                          <p className="text-[10px] font-medium text-[#10b981]">
                            {t('Shipped Qty: {n}', { n: item.shippedQuantity })}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {!isDistributor && Number(item.product?.price) > 0 && (
                    <p className="text-xs font-semibold text-[#0089ff]">
                      {money(item.product.price)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {isDistributor && <DistributorSaleDetails order={order} t={t} locale={locale} />}
          {adminEditable && allProducts.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#f0f2f5]">
              <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider mb-2">
                {t('Add Product')}
              </p>
              <div className="flex items-center gap-2">
                <select
                  aria-label={t('Product to add')}
                  value={selectedProductToAdd}
                  onChange={(e) => setSelectedProductToAdd(e.target.value)}
                  className="flex-1 h-[32px] border border-[#e2e8f0] rounded-[6px] px-2 text-xs text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff] bg-white"
                >
                  <option value="">{t('Select a product...')}</option>
                  {allProducts
                    .filter(
                      (p) =>
                        !editableLineItems.some(
                          (item) => (item.product?._id || item.product) === p._id
                        )
                    )
                    .map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                </select>
                <button
                  onClick={handleAddProduct}
                  disabled={!selectedProductToAdd}
                  className="h-[32px] px-3 bg-[#0089ff] text-white text-xs font-medium rounded-[6px] hover:bg-[#0077e6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('Add')}
                </button>
              </div>
            </div>
          )}
        </div>

        <OrderTrackingDetails order={order} t={t} />

        <div className="h-px bg-[#e2e8f0] mb-4" />

        {shippingId(order._id) && (
          <ShippingDocuments orderId={order._id} adminDistributorId={adminDistributorId} />
        )}

        {isDistributor && <DistributorOrderHistory order={order} t={t} locale={locale} />}

        {!isDistributor && (
          <>
            <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider mb-3">
              {t('Sales Representative')}
            </p>
            {salesRepName ? (
              <div className="flex items-center gap-3 mb-5">
                <div className="w-[34px] h-[34px] rounded-full bg-[#e6f1ff] flex items-center justify-center text-[13px] font-semibold text-[#0f172a]">
                  {initials}
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-[#0f172a]">{salesRepName}</p>
                  <p className="text-xs font-medium text-[#64748b]">
                    {order.salesRep?.email || ''}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-[13px] font-semibold text-[#0f172a] mb-5">—</p>
            )}
          </>
        )}

        {order.comment && (
          <div className="mb-4">
            <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider mb-2">
              {t('Note from Customer')}
            </p>
            <div className="rounded-[10px] px-4 py-3 text-xs font-medium bg-[#f9fafc] border border-[#e2e8f0] text-[#0f172a]">
              {order.comment}
            </div>
          </div>
        )}

        {isDistributor && !adminDistributorId && order.status === 'submitted' && (
          <DistributorHeldOrder
            order={order}
            admin={canAdminister}
            onChanged={() => {
              onAction?.();
              onClose();
            }}
            onBusy={setActionLoading}
          />
        )}

        {/* Submitted orders — Approve or Cancel (admin only) */}
        {canAdminister && !isDistributor && isSubmitted && (
          <>
            <p className="text-[12px] font-medium text-[#0f172a] mb-2">
              {t('Note from MedEffects')}
            </p>
            <textarea
              aria-label={t('Note from MedEffects')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('Add a note...')}
              className="w-full h-[80px] border border-[#e2e8f0] rounded-[10px] px-4 py-3 text-xs text-[#0f172a] placeholder:text-[#97a3b6] resize-none focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff] mb-5"
            />
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="h-[42px] px-5 bg-[#f23e41] text-white text-sm font-semibold rounded-[10px] hover:bg-[#d93235] transition-colors disabled:opacity-50"
              >
                {t('Cancel Order')}
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="h-[42px] px-5 bg-[#10b981] text-white text-sm font-semibold rounded-[10px] hover:bg-[#059669] transition-colors disabled:opacity-50"
              >
                {t('Approve')}
              </button>
            </div>
          </>
        )}

        {/* Approved orders — Ship or Cancel (admin only) */}
        {canAdminister && isApproved && (
          <>
            <label className="block text-[12px] font-medium text-[#0f172a] mb-2">
              {t('Carrier')}
              <select
                value={shippingCarrier}
                disabled={actionLoading}
                onChange={(event) => {
                  setShippingCarrier(event.target.value);
                  setShippingError('');
                }}
                className="mt-2 w-full h-[42px] border border-[#e2e8f0] rounded-[10px] px-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20"
              >
                <option value="">{t('Choose carrier')}</option>
                <option value="fedex">FedEx</option>
                <option value="ups">UPS</option>
                <option value="other">{t('Other / Not sure')}</option>
              </select>
            </label>
            <p className="text-[12px] font-medium text-[#0f172a] mb-2">{t('Tracking Number')}</p>
            <input
              aria-label={t('Tracking Number')}
              value={trackingNumber}
              disabled={actionLoading}
              maxLength={1000}
              onChange={(e) => {
                setTrackingNumber(e.target.value);
                setShippingError('');
              }}
              placeholder={t('Enter tracking number...')}
              className="w-full h-[42px] border border-[#e2e8f0] rounded-[10px] px-4 text-xs text-[#0f172a] placeholder:text-[#97a3b6] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff] mb-4"
            />
            {shippingError && (
              <p role="alert" className="mb-3 text-xs text-red-700">
                {t(shippingError)}
              </p>
            )}
            {!isDistributor && <DirectOrderShippingNote t={t} note={note} onChange={setNote} />}
            <div className="flex items-center justify-center gap-3">
              {!isDistributor && (
                <DirectOrderCancelButton t={t} onCancel={handleCancel} disabled={actionLoading} />
              )}
              <button
                onClick={handleShip}
                disabled={actionLoading}
                className="h-[42px] px-5 bg-[#10b981] text-white text-sm font-semibold rounded-[10px] hover:bg-[#059669] transition-colors disabled:opacity-50"
              >
                {t('Ship')}
              </button>
            </div>
          </>
        )}

        {!adminEditable && (
          <>
            {existingAdminNote && (
              <div className="mb-4">
                <p className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider mb-2">
                  {t('Note from MedEffects')}
                </p>
                <div className="rounded-[10px] px-4 py-3 text-xs font-medium bg-[#f0f9ff] border border-[#bae0ff] text-[#0f172a]">
                  {existingAdminNote}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
